import { useHistory } from "react-router-dom";
import React, { useContext } from "react";
import useSymbolAndAccountSelectors from "features/bots/BotForm/common/useSymbolAndAccountSelectors";
import { getCoinExtraDetailsRoute } from "utils/routes";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import ConnectedAmountPercentInputs from "common/Inputs/ConnectedAmountPercentInputs";
import useGeneralTradeOptions from "features/tradingTerminal/General/useGeneralTradeOptions";
import { confirmWithCallback, openMessage } from "utils/misc";
import Modal from "common/Modal/Modal";
import useSharedGeneral from "@shared/features/tradingTerminal/common/hooks/useSharedGeneral";
import { ErrorText } from "features/bots/common/styles";
import { ON_CHANGE_WARNING_MESSAGE } from "@shared/features/tradingTerminal/common/constants";
import { ErrorTypes } from "@shared/features/trades/common/types";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useSignalSymbolAndExchangesSelectors from "features/tradingTerminal/General/useSignalSymbolAndExchangesSelectors.tsx";

const useGeneral = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const history = useHistory();
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();
  const { isSignal, valuesModified } = useContext(TradingTerminalContext)!;

  const onChangeWithWarning = async (func: () => void) => {
    if (valuesModified) {
      const okPressed = await confirmWithCallback(
        confirmModal,
        t(ON_CHANGE_WARNING_MESSAGE),
        undefined,
        undefined,
        t("buttons.yes"),
        t("buttons.no")
      );
      if (okPressed) func();
    } else {
      func();
    }
  };

  const {
    amountInputSharedProps,
    quantityInputSharedProps,
    onAddExistingAmountClick,
    onRemoveExistingAmountClick,
    amountInputTitle,
    quantityInputTitle,
    showExistingAmount,
    advancedExpanded,
    setAdvancedExpanded,
    generalErrors,
    isLoading,
    isFetching,
    amountValidationMessage,
    quantityValidationMessage,
    existingQuantityWarningMessage,
    amountCoin,
    quantityCoin,
    symbols,
    onExistingQuantityClick,
    invalidTradeAmount,
    invalidTradeQuantity,
    maxNumTradesExceeded,
    useTradingTerminalOldVersion,
    amountInitialized,
    symbolAndAccountSelectorsData,
    hasError,
  } = useSharedGeneral({
    openMessage,
    useSymbolAndAccountSelectors: isSignal
      ? undefined
      : useSymbolAndAccountSelectors,
    onChangeWithWarning,
  });
  const signalSymbolAndAccountSelectorsData = (
    isSignal ? useSignalSymbolAndExchangesSelectors : undefined
  )?.({ onChangeWithWarning });

  const {
    accountSelector,
    accountError,
    accountSelectorTitle,
    symbolSelector,
    symbolError,
    symbolSelectorTitle,
  } =
    symbolAndAccountSelectorsData || signalSymbolAndAccountSelectorsData || {};

  const maxNumTradesForSymbolError = maxNumTradesExceeded && (
    <ErrorText>{generalErrors[ErrorTypes.MAX_NUM_TRADES_EXCEEDED]}</ErrorText>
  );

  const {
    longShort,
    leverage,
    cancelOnTp,
    tradeType,
    showTradeType,
    showGeneralOptions,
    marginTypeErrorMessage,
    multiplierErrorMessage,
    marginErrorReason,
    leverageWarningModal,
  } = useGeneralTradeOptions(confirmModal);

  const openSymbolDrawer = () => {
    return history.push(
      getCoinExtraDetailsRoute(location.pathname, symbols[0])
    );
  };

  const amountInput = (
    <ConnectedAmountPercentInputs
      {...amountInputSharedProps}
      showSlider={useTradingTerminalOldVersion}
      bordered={true}
      focusOnMount={!invalidTradeAmount}
      icon={
        amountCoin && (
          <IconWithFallback
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={amountCoin}
            iconStyle={{ marginRight: 4 }}
          />
        )
      }
      validationMessage={
        amountInitialized && (
          <Text
            $size={typography.fontSizes.S}
            type={invalidTradeAmount ? "danger" : "secondary"}
          >
            {!hasError && amountValidationMessage}
          </Text>
        )
      }
    />
  );

  const quantityInput = (
    <ConnectedAmountPercentInputs
      {...quantityInputSharedProps}
      showSlider={useTradingTerminalOldVersion}
      bordered={true}
      focusOnMount={invalidTradeAmount && !invalidTradeQuantity}
      icon={
        quantityCoin && (
          <IconWithFallback
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={quantityCoin}
            iconStyle={{ marginRight: 4 }}
          />
        )
      }
      validationMessage={
        <Text
          $size={typography.fontSizes.S}
          type={
            invalidTradeQuantity && !existingQuantityWarningMessage
              ? "danger"
              : "secondary"
          }
        >
          {!hasError && quantityValidationMessage}
        </Text>
      }
    />
  );

  return {
    accountSelector,
    accountError,
    symbolSelector,
    symbolError,
    accountSelectorTitle,
    symbolSelectorTitle,
    amountInput,
    amountInputTitle,
    quantityInput,
    quantityInputTitle,
    openSymbolDrawer,
    showExistingAmount,
    onAddExistingAmountClick,
    onRemoveExistingAmountClick,
    isLoading,
    isFetching,
    longShort,
    leverage,
    cancelOnTp,
    tradeType,
    showTradeType,
    advancedExpanded,
    setAdvancedExpanded,
    onExistingQuantityClick,
    amountCoin,
    quantityCoin,
    confirmModalContextHolder,
    hasError,
    showGeneralOptions,
    maxNumTradesForSymbolError,
    marginTypeErrorMessage,
    multiplierErrorMessage,
    marginErrorReason,
    amountValidationMessage,
    invalidTradeAmount,
    leverageWarningModal,
  };
};

export default useGeneral;
